import React from 'react';
import { Box, Chip } from '@drone-js/core';
import { useSelector } from 'react-redux';
import { selectBehaviorFromBootstrap } from 'store/slice/bootstrap';
import { OfferStartTimeDisplayBehavior } from 'core/type/behaviors.types';
import { convertTimeTo12HourFormat } from 'core-framework/util/datetime/date';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesAvailableOptionCardTimeClickEventActivitiesFieldsV5 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesAvailableOptionCardTimeClickEventActivitiesManifestV5 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { getTimeslotFromSpecification } from 'component/page/detail/PackageOptions/PackageOptionsUtils';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { killEvent } from 'core/utils/domUtils';

interface OwnProps {
    isOfferSelected: boolean;
    id: number;
    onTimeSelected: (index: number) => void;
    isOfferAvailable: boolean;
    activityId: number | undefined;
    enrichedOfferOption?: EnrichedOfferOption;
    startTime?: string;
    isEnableOfferGrouping?: boolean;
    selectedOfferId?: number;
}

const OfferChip: React.FC<OwnProps> = (props) => {
    const { enrichedOfferOption, isOfferSelected, isOfferAvailable, activityId } = props;
    const isEnableOfferGrouping = props.isEnableOfferGrouping ?? false;
    const behavior = useSelector(selectBehaviorFromBootstrap);
    const shouldUse12HourFormat = behavior.availability.offerStartTimeFormat === OfferStartTimeDisplayBehavior.TwelveHour;
    const specification = enrichedOfferOption?.specification;
    const startTime = isEnableOfferGrouping ? props.startTime : getTimeslotFromSpecification(specification);
    const timeParts = convertTimeTo12HourFormat(startTime);
    const isAvailable = isEnableOfferGrouping ? isOfferAvailable : enrichedOfferOption?.isAvailable;

    if (!startTime) {
        return null;
    }

    const onOfferChipClick = (e: React.MouseEvent) => {
        trackElementEvent<ActivitiesAvailableOptionCardTimeClickEventActivitiesFieldsV5>(
            ActivitiesAvailableOptionCardTimeClickEventActivitiesManifestV5.ids,
            {},
            { action_element_index: props.id + 1, activities_start_time: startTime || '', offer_id: props.selectedOfferId, activity_id: activityId },
        );
        props.onTimeSelected(props.id);
        killEvent(e);
    };

    const isDisabled = !isOfferAvailable || !isAvailable;

    return (
        <Box left="4px" position="relative">
            <Chip
                onClick={onOfferChipClick}
                mx={isEnableOfferGrouping ? '0' : '4'}
                my="4"
                data-element-name="activities-available-option-card-time"
                data-testid="activities-available-option-card-time"
                data-element-index={props.id + 1}
                state={isDisabled ? 'disabled' : 'enabled'}
                size="s"
                aria-pressed={isOfferSelected}
                aria-label={shouldUse12HourFormat ? `${timeParts.time} ${timeParts.meridiem}` : startTime}
                label={shouldUse12HourFormat ? `${timeParts.time} ${timeParts.meridiem}` : startTime}
                selection={isOfferSelected ? 'selected' : 'unselected'}
            />
        </Box>
    );
};
OfferChip.displayName = 'OfferChip';

export default OfferChip;
